/**
 * `TerminalConfig` — the single object a host passes to `<Terminal config={…} />`.
 *
 * The component owns ALL the wiring: from this config it builds the
 * `@paradigm/terminal-core` device-identity store, agent-url/token store, and
 * chat-history store, and (per connection) the OpenClaw WebSocket client. Hosts
 * never touch core factories.
 *
 * Design goals:
 *  - Minimal required surface. A host supplies identity + namespace + gateway;
 *    everything else has sensible defaults.
 *  - The default `keyFor` reproduces Paradex's historical localStorage key
 *    shapes (`<namespace>_terminal_chat_history:<addr>:<sid>`, …) so existing
 *    chat history loads with zero migration when `namespace === 'paradex'`.
 *  - Host-coupled extras (generative-UI components, positions feed, subkey
 *    card, session-sync, model catalog) are OPTIONAL injections; when absent
 *    the corresponding feature is simply off.
 */
import {
  createAgentUrlStore,
  createChatHistoryStore,
  createDeviceIdentityStore,
  createLegacyDeviceKeyMigrator,
  createMemoryStorage,
  createPreferencesStore,
  type AgentUrlStore,
  type ChatHistoryStore,
  type DeviceIdentityStore,
  type PreferencesStore,
  type SessionFolder,
  type TerminalPreferences,
} from '@paradigm/terminal-core';
import type { ComponentType } from 'react';

import type {
  ChatErrorCategory,
  ChatErrorReport,
  ChatErrorSignal,
  RunOutcomeReport,
} from './telemetry/chat-errors.js';

/**
 * How a stored secret reaches the upstream request — the two modes iron-proxy's
 * secrets transform supports.
 *
 * `inject` is the default and needs nothing from the agent: the proxy attaches
 * the secret to any request to an allowed host, so there is no placeholder and
 * nothing for a user to carry anywhere. `replace` exists for what inject cannot
 * express — the agent choosing between two secrets on one host, or a secret that
 * belongs inside a request body — and is the only mode with a placeholder.
 */
export type CredentialDelivery =
  | {
      readonly mode: 'inject';
      /** Exactly one of these: where the secret is written. */
      readonly header?: string;
      readonly queryParam?: string;
      /** Optional wrapper, e.g. `Bearer {{ .Value }}`; must mention `.Value`. */
      readonly formatter?: string;
    }
  | {
      readonly mode: 'replace';
      /** Where the proxy looks for the placeholder. At least one. */
      readonly matchHeaders?: readonly string[];
      readonly matchBody?: boolean;
      readonly matchPath?: boolean;
      readonly matchQuery?: boolean;
    };

/**
 * One stored credential as the Credentials pane renders it. Never its value —
 * nothing in this tree can decrypt a stored credential, and no host injection
 * is allowed to hand one back.
 */
export interface StoredCredentialView {
  readonly id: string;
  readonly label: string;
  /**
   * The string the agent writes where the secret belongs — REPLACE mode only,
   * empty in inject mode where the proxy attaches the secret by destination.
   * Shown so the user can see what their agent will use, and paste it directly
   * if publishing it to the agent failed.
   */
  readonly placeholder: string;
  readonly allowedHosts: readonly string[];
  /**
   * Request paths this credential is narrowed to, within those hosts. Empty
   * means any path, which is both the default and what every credential
   * enrolled before this field existed does.
   *
   * More than cosmetic on a replace-mode credential: the rule that matches is
   * also the rule that REQUIRES the placeholder, so a host with no path list
   * turns that host's public endpoints into refusals too.
   */
  readonly allowedPaths: readonly string[];
  readonly delivery: CredentialDelivery;
  readonly createdAt: string;
  /**
   * Whether the agent has this credential's placeholder yet. `false` renders a
   * "not on your agent yet" marker on that row — the credential is stored and
   * the proxy will substitute it, and the server is republishing in the
   * background, so it is a state to report rather than an error to fix.
   * `undefined` means unknown (the agent could not be asked, or the host has
   * no agent), and the row makes no claim either way.
   */
  readonly agentReady?: boolean;
}

/**
 * Result of storing a credential. A rejection is a message the pane shows
 * inline — the server says which of label, hosts or limit was the problem — as
 * distinct from a thrown error, which is an unexpected failure.
 */
export type StoreCredentialOutcome =
  | {
      readonly kind: 'stored';
      readonly credential: StoredCredentialView;
      /**
       * Whether the placeholder reached the user's agent. Enrollment publishes
       * it, so this is normally true; false means the credential works but the
       * agent has not been told, and the pane says so. Omitted by a host that
       * has no agent-publishing mechanism — the pane then makes no claim
       * either way rather than warning about something that cannot happen.
       */
      readonly agentReady?: boolean;
    }
  | { readonly kind: 'rejected'; readonly message: string };

/**
 * Result of correcting one — its hosts and delivery, never its name or its
 * value. Same shape as storing, and for the same reason: `rejected` is a
 * message the pane shows inline (the server says which field was wrong), and
 * `agentReady` false means the change is saved and live but the agent has not
 * been told the placeholder moved.
 */
export type UpdateCredentialOutcome =
  | {
      readonly kind: 'updated';
      readonly credential: StoredCredentialView;
      readonly agentReady?: boolean;
    }
  | { readonly kind: 'rejected'; readonly message: string }
  | { readonly kind: 'not-found' };

/**
 * Result of revoking one. `partial` means the credential is revoked but its
 * stored copy could not be deleted — the pane says so rather than claiming a
 * clean delete.
 */
export type RevokeCredentialOutcome =
  | { readonly kind: 'revoked' }
  | { readonly kind: 'not-found' }
  | { readonly kind: 'partial'; readonly message: string };

/** Feature toggles for whole subsystems. All default to `false`/off. */
export interface TerminalFeatureFlags {
  /** Dispatch `ui.render` catalog/MCP specs. Needs `components` to render. */
  readonly generativeUi?: boolean;
  /** Custom MCP-app iframe tabs (`app.register`). */
  readonly mcpApps?: boolean;
  /** Cron tab. */
  readonly cron?: boolean;
  /**
   * The appearance controls the HOST must enact: theme, UI scale, and font.
   * Off by default because the host owns the CSS variables (and the zoom, and
   * the font stack) the components style against — the terminal only records
   * the choice. Turn this on only if you actually apply all three from
   * `onPreferencesChange`; leaving it off keeps dead controls out of an embed
   * whose look its host controls. The Appearance section itself stays either
   * way: its Language row is applied by the terminal on the wire and needs
   * nothing from the host.
   */
  readonly appearance?: boolean;
}

/**
 * Optional host capability injections. Each is independent; omit to disable
 * the feature that depends on it.
 */
export interface TerminalCapabilities {
  // The model picker is fully native and takes no host injection: it fetches
  // the live catalog from the gateway's `/api/models` (`fetchGatewayModels`),
  // labels ids via `defaultModelLabel`, and defaults to Opus. The gateway is
  // the ONLY source — there is no built-in fallback list, so the picker shows a
  // placeholder until the catalog lands rather than a hardcoded guess that
  // could name models this gateway does not serve.
  /** Live positions feed for position-backed widgets. Omit ⇒ those widgets hidden. */
  readonly positionsFeed?: { usePositions(): readonly unknown[] };
  /**
   * Catalog of components the generative-UI renderer mounts by key — an
   * `AgentSpec` child `{ component: 'metric_card', props }` renders
   * `components['metric_card']` with `props`. Omit (with `features.generativeUi`
   * off) ⇒ catalog disabled. Unknown keys render a small fallback.
   */
  readonly components?: Readonly<
    Record<string, ComponentType<Record<string, unknown>>>
  >;
  /**
   * Registry for legacy `widgetType` chat messages — a message with
   * `widgetType: 'order_preview'` renders `widgets['order_preview']` with the
   * message's `widgetData`. Omit ⇒ widget messages show a placeholder.
   */
  readonly widgets?: Readonly<
    Record<string, ComponentType<{ data: Record<string, unknown> }>>
  >;
  /** Host-provided icons; the component ships defaults. */
  readonly icons?: Readonly<Record<string, ComponentType<unknown>>>;
}

export interface TerminalConfig {
  /**
   * Called with the user's settings on mount and on every change, including
   * changes that arrived from another device via sync.
   *
   * This is how a host applies `theme`: the terminal records the choice but
   * cannot enact it, since the CSS variables its components style against
   * belong to the host. A callback rather than a hook because the host shell
   * renders OUTSIDE the terminal's tree (and the cloud path mounts `Terminal`
   * indirectly, via `DimeCloudApp`), so there is nowhere for it to call a hook
   * from. Fires on both paths, because both take this config.
   *
   * Must be stable (`useCallback`) — an unstable identity re-fires the effect
   * on every render.
   */
  readonly onPreferencesChange?: (preferences: TerminalPreferences) => void;

  /**
   * Persist a change to the signed-in user's profile. Omit and the Profile
   * section renders read-only.
   *
   * The terminal does NOT own the profile — `user` below is host-supplied, and
   * on the cloud path it is Clerk's. So this follows the same rule as the
   * theme: the terminal renders the field and reports the edit, the host
   * performs the write against whoever actually owns the identity. Storing an
   * editable copy locally instead would give a display name two sources of
   * truth, and the user would see one name while everyone else saw another.
   *
   * Reject the promise to surface a failure in the form; the section keeps the
   * user's text so nothing they typed is lost. Only fields the user can
   * actually change appear here — an email is a sign-in credential, and
   * changing it is a re-verification flow that belongs to the identity
   * provider, not to a settings pane.
   */
  readonly onUpdateProfile?: (patch: {
    readonly displayName: string;
  }) => Promise<void>;

  /**
   * Stored venue API credentials, for the Credentials tab of Apps & Skills.
   * Omit and that tab is absent, leaving All and Installed.
   *
   * A host injection rather than something the terminal does itself, for the
   * same reason `onUpdateProfile` is: the terminal does not own this data. The
   * credentials live behind the DIME web-api, reachable only with a DIME JWT
   * the host mints, so the cloud path supplies these four calls
   * (`DimeCloudTerminal` wires them from `DimeCloudClient`) and an embed whose
   * host provides its own gateway supplies nothing and shows no pane.
   *
   * `store` takes the credential in PLAINTEXT and must seal it before it leaves
   * the browser — see `sealCredential` in `@paradigm/terminal-core`. This
   * component keeps the plaintext only for the duration of the call: it is
   * never written to preferences, chat history, or any store.
   */
  readonly credentials?: {
    readonly list: () => Promise<readonly StoredCredentialView[]>;
    readonly store: (input: {
      readonly label: string;
      readonly allowedHosts: readonly string[];
      /**
       * Request paths this credential is narrowed to, within those hosts. Empty
       * means any path, which is both the default and what every credential
       * enrolled before this field existed does.
       *
       * More than cosmetic on a replace-mode credential: the rule that matches is
       * also the rule that REQUIRES the placeholder, so a host with no path list
       * turns that host's public endpoints into refusals too.
       */
      readonly allowedPaths: readonly string[];
      readonly delivery: CredentialDelivery;
      readonly secret: string;
    }) => Promise<StoreCredentialOutcome>;
    /**
     * Correct an existing credential's destination and delivery. Takes no
     * secret and no label: the name is part of what the ciphertext is sealed
     * to, so it cannot change, and changing the value means enrolling a new
     * credential. Omitted by a host whose backend has no update endpoint —
     * the pane then offers no Edit control rather than one that fails.
     */
    readonly update?: (
      id: string,
      input: {
        readonly allowedHosts: readonly string[];
        /**
         * Request paths this credential is narrowed to, within those hosts. Empty
         * means any path, which is both the default and what every credential
         * enrolled before this field existed does.
         *
         * More than cosmetic on a replace-mode credential: the rule that matches is
         * also the rule that REQUIRES the placeholder, so a host with no path list
         * turns that host's public endpoints into refusals too.
         */
        readonly allowedPaths: readonly string[];
        readonly delivery: CredentialDelivery;
      },
    ) => Promise<UpdateCredentialOutcome>;
    readonly revoke: (id: string) => Promise<RevokeCredentialOutcome>;
  };

  // ──────── Identity ────────
  /** Account scope for storage keys + client id. `null` ⇒ signed out (no auto-connect). */
  readonly accountId: string | null;
  /** Bearer token for session-sync / shim REST (NOT the gateway device token). */
  readonly getAccessToken?: () => Promise<string | null>;
  /** Subscribe to account changes so the component re-hydrates. Returns an unsubscribe. */
  readonly onAccountChange?: (cb: () => void) => () => void;
  /**
   * Signed-in user's display identity (sidebar account button, account menu,
   * empty-state greeting). Cloud hosts pass the IdP profile (e.g. Clerk name
   * + email + avatar). Omit ⇒ the UI falls back to deriving a label from the
   * gateway URL — only meaningful for per-user gateway hosts (manual pairing,
   * `<name>-<hash>.terminal.dime.gg`); behind the shared gateway-proxy every
   * URL is the same host, so cloud hosts should always provide this.
   */
  readonly user?: {
    /** e.g. Clerk first/full name. Falls back to `email`'s local part. */
    readonly displayName?: string;
    /** Shown in the account-menu header instead of the gateway URL. */
    readonly email?: string;
    /** Real profile picture; replaces the bundled placeholder avatars. */
    readonly avatarUrl?: string;
  };

  // ──────── Namespacing / storage ────────
  /** localStorage namespace, e.g. `'paradex' | 'paradigm' | 'standalone'`. Drives the default key shapes + device key. */
  readonly namespace: string;
  /** Override the default key shape. Defaults to Paradex's historical convention. */
  readonly keyFor?: (
    scope: string,
    accountId: string,
    ...extra: string[]
  ) => string;
  /** Optional one-time legacy-key migration (e.g. Paradigm's suffix shape). */
  readonly legacyKeyFor?: (
    scope: string,
    accountId: string,
    ...extra: string[]
  ) => string;
  /** Default folder for sessions created by this host. Defaults to `namespace`. */
  readonly defaultFolder?: string;

  // ──────── Gateway ────────
  /** Explicit gateway ws(s) URL (wins over `resolveUrl`). */
  readonly gatewayUrl?: string | null;
  /** Fallback URL resolver when no explicit/stored URL exists. */
  readonly resolveUrl?: (accountId: string) => Promise<string | null>;
  /** Pairing-approval display prefix + client-id prefix. e.g. `'paradex-dime-terminal'`. */
  readonly clientIdPrefix: string;
  /** Connection-health strategy. Default `'heartbeat'`. */
  readonly healthStrategy?: 'heartbeat' | 'circuit-breaker';
  /** Enable the pairing-token retry / pairing-role fallback path. Default `true`. */
  readonly supportsPairingRole?: boolean;
  /** Opaque catalog manifest answered on `ui.catalog.request` (only when generativeUi on). */
  readonly catalogManifest?: unknown;
  /**
   * One-time gateway token used for the handshake when local storage has none
   * yet (e.g. a standalone "paste your gateway URL + token" pairing). Seeded
   * into the token store on first successful auth, so reconnects don't need it.
   */
  readonly fallbackPairingToken?: string;
  /**
   * Mint a fresh gateway credential immediately before every ws handshake.
   * For hosts whose credential is a short-TTL bearer token (the cloud flow's
   * 5-minute DIME JWT presented to the gateway-proxy): without it, reconnects
   * replay the stale connect-time token and fail with "invalid token". A
   * non-empty result wins over the stored/fallback token; null (or a throw)
   * falls back to the stored credential. Omit for long-lived gateway tokens
   * (manual pairing, direct-gateway grants).
   */
  readonly resolveHandshakeToken?: () => Promise<string | null>;
  /**
   * Route sidecar HTTP calls (model catalog `GET /api/models`, session sync
   * `/sync/v1/*`, personalization `/personalization/v1/*`) through a backend
   * forward instead of the gateway host derived from the ws URL. `baseUrl` is
   * prepended verbatim (no trailing slash) to the sidecar paths;
   * `getAuthHeaders` supplies the auth headers for every call — return null
   * when no credential is currently available (session sync treats that as
   * transient and retries; personalization shows a retryable error). Lets a
   * cloud host keep the gateway URL + credential entirely server-side. Omit ⇒
   * sidecar calls go directly to the gateway with the stored agent token.
   */
  readonly sidecar?: {
    readonly baseUrl: string;
    readonly getAuthHeaders: () => Readonly<Record<string, string>> | null;
  };
  /**
   * When true, the gateway URL / pairing token / device token live in memory
   * only — nothing connection-related is written to `localStorage`. For hosts
   * whose connection grant is resolved server-side each session (e.g. the
   * standalone's cloud-auth flow) and must leave no local trace. Chat history
   * persistence is unaffected. Static per host — changing it at runtime does
   * not rebuild the stores.
   */
  readonly ephemeralConnection?: boolean;

  // ──────── Prompt context ────────
  //
  // Two independent framing channels — pick ONE for a given piece of framing:
  //  • `systemPreamble` — free-text, prepended to EVERY turn's payload.
  //  • `sessionContext` — one-shot key/value, sent once right after auth.
  // They do not coordinate: a host that puts the same framing in both sends it
  // twice on turn 1 (no agent-side dedup is assumed). Use `systemPreamble` for
  // durable identity/output-style that must survive long sessions; use
  // `sessionContext` for one-time operator metadata.
  /**
   * Static framing prepended to EVERY outbound send's gateway payload (not
   * shown in the chat bubble — the user still sees only what they typed). Use
   * to tell a generic agent what domain it's operating in and how to answer
   * (e.g. Paradigm's block-trade identity + terse-output directive), re-asserted
   * on every turn. Blank / whitespace-only ⇒ no-op. See also `sessionContext`.
   */
  readonly systemPreamble?: string;
  /**
   * One-shot payload sent right after auth as
   * `<session-context>k1=v1;k2=v2;…</session-context>` (e.g. the operator
   * identity / output style). Bypasses visible chat history. See also
   * `systemPreamble` (setting both double-sends framing on the first turn).
   */
  readonly sessionContext?: Record<string, string>;
  /** Dedup key so heartbeat reconnects don't re-send; defaults to `accountId`. */
  readonly sessionContextDedupKey?: string;

  // ──────── Side effects ────────
  /** Persist a freshly-paired agent URL to the host profile. */
  readonly persistAgentUrl?: (url: string) => void;
  /**
   * Host "log out / unpair" action, surfaced as the "Log Out" item in the
   * sidebar account menu. Omit ⇒ the menu hides that action.
   */
  readonly onLogout?: () => void;
  /**
   * Label for the `onLogout` action (sidebar menu + stuck-phase escape link).
   * Default "Log Out". Use when the action's real semantics differ — e.g. the
   * native-auth silent path labels its packaged unlink action
   * "Unlink account", since a tethered session has nothing to log out of.
   */
  readonly logoutLabel?: string;
  /**
   * Optional host "install / download the PWA" action, surfaced as a "Download
   * App" item in the sidebar account menu. The host owns the install hook
   * (e.g. `beforeinstallprompt` capture) and should pass this only when an
   * install is actually offered (Chromium with a deferred prompt) and the app
   * isn't already running standalone — so the menu item appears only when
   * clicking it would do something.
   */
  readonly onDownloadApp?: () => void;
  /** Pipe errors to the host's logging. */
  readonly logException?: (
    error: unknown,
    ctx?: Record<string, unknown>,
  ) => void;

  /**
   * Every CHAT failure the user is shown — a run that errored, a gateway error
   * that reached the chat, a send the client refused. Reports arrive scrubbed
   * and bucketed. Must not throw.
   *
   * Separate from `logException`, which is thrown JavaScript: "is the code
   * broken?" vs "is the agent answering?", and mixing them buries the second.
   */
  readonly onChatError?: (report: ChatErrorReport) => void;

  /**
   * Every run this client SENDS, when it ends. The denominator: `onChatError`
   * without it gives a count that cannot say whether anything is degrading.
   *
   * Honours the user's tier like `onChatError`, except `minimal` still emits,
   * stripped to outcome, duration and category — an opt-out-able denominator
   * would improve as real reliability got worse. Must not throw.
   */
  readonly onRunOutcome?: (report: RunOutcomeReport) => void;

  /**
   * Names the agent backend when the gateway does not name itself, so a
   * backend swap is visible in the data. The handshake wins over this.
   */
  readonly harness?: {
    readonly name?: string;
    readonly version?: string;
  };

  /**
   * Classify a failure the backend-neutral built-ins don't recognise. Runs
   * BEFORE them and wins; `null` falls through, so a host can special-case two
   * codes without reimplementing the rest.
   *
   * Must be pure and must not throw — it runs inside the frame handler.
   */
  readonly classifyChatError?: (
    signal: ChatErrorSignal,
  ) => ChatErrorCategory | null;

  /**
   * Optional connection diagnostics hook. Receives gateway connection
   * lifecycle events (`connect`, `open`, `close` with code/reason, `error`,
   * `retries-exhausted`, and `status` transitions) so a host can log why a
   * connection stalls. Omit ⇒ silent.
   */
  readonly onConnectionDebug?: (
    event: string,
    detail?: Record<string, unknown>,
  ) => void;

  // ──────── Optional ────────
  readonly features?: TerminalFeatureFlags;
  readonly capabilities?: TerminalCapabilities;
}

// ── Default key shapes (match Paradex's historical localStorage keys) ──
// `<namespace>_<infix>:<accountId>[:<extra…>]`
const SCOPE_INFIX: Readonly<Record<string, string>> = {
  // agent-url store scopes
  agentUrl: 'ai_agent_url',
  agentToken: 'ai_agent_token',
  deviceToken: 'ai_agent_device_token',
  // preferences store scope. Settings the user chose on purpose (default
  // model, theme) persist like chat history rather than like credentials:
  // `ephemeralConnection` covers the gateway URL/token, which must leave no
  // trace — a remembered theme is not that, and a settings screen that forgets
  // on reload is not a settings screen.
  preferences: 'terminal_preferences',
  // the Personalization editor's unsaved drafts, one key per file
  personalizationDrafts: 'terminal_personalization_drafts',
  // chat-history store scopes
  sessions: 'terminal_sessions',
  customFolders: 'terminal_custom_folders',
  messages: 'terminal_chat_history',
  // The agent's own rows for a chat, cached verbatim for first paint. A
  // separate key from `messages` on purpose: that one held a list this client
  // had transformed and mixed device-only bubbles into, so the two cannot be
  // read as each other. Blobs under the old key are deleted on upgrade.
  transcript: 'terminal_chat_transcript',
  historyUi: 'terminal_history_ui',
  tombstones: 'terminal_tombstones',
  legacyHistory: 'terminal_chat_history',
  veryLegacyHistory: 'terminal_chat_history',
  pendingCleanup: 'terminal_pending_cleanup',
};

/** Build the Paradex-shaped default `keyFor` for a namespace. */
export function makeDefaultKeyFor(
  namespace: string,
): (scope: string, accountId: string, ...extra: string[]) => string {
  return (scope, accountId, ...extra) => {
    const infix = SCOPE_INFIX[scope] ?? scope;
    return [`${namespace}_${infix}`, accountId, ...extra].join(':');
  };
}

/** The core stores the component wires up from a `TerminalConfig`. */
export interface TerminalStores {
  readonly urlStore: AgentUrlStore;
  readonly historyStore: ChatHistoryStore;
  readonly deviceStore: DeviceIdentityStore;
}

/**
 * The preferences store on its own. Kept out of `buildTerminalStores` because
 * that one carries side effects (the legacy-key sweep, the device-key IDB
 * migrator) and must be called once per namespace, whereas this is a pure
 * closure over `keyFor` — so the settings provider and the agent provider can
 * each build one without stepping on each other.
 */
export function buildPreferencesStore(
  config: TerminalConfig,
): PreferencesStore {
  return createPreferencesStore({
    keyFor: config.keyFor ?? makeDefaultKeyFor(config.namespace),
  });
}

/**
 * Build the device-identity, agent-url, and chat-history stores from a config.
 * Stable for a given `namespace`; the component memoizes on that key.
 */
export function buildTerminalStores(config: TerminalConfig): TerminalStores {
  const keyFor = config.keyFor ?? makeDefaultKeyFor(config.namespace);
  const deviceKey = `${config.namespace}_terminal_device`;

  // The `selectedModel` scope is gone (each session's model now lives on the
  // gateway), so nothing can address its key again — including for hosts whose
  // stated contract is to leave no local trace, where the removed
  // `modelStorage` wiring wrote it to localStorage even in ephemeral mode.
  // Sweep it rather than orphaning a dead key on every returning user.
  if (config.accountId != null) {
    // A host with its own `keyFor` still maps the retired scope; the default
    // one no longer does (the `SCOPE_INFIX` entry went with it), so fall back
    // to the literal historical shape rather than deriving a key that never
    // existed.
    const legacyModelKey =
      config.keyFor?.('selectedModel', config.accountId) ??
      `${config.namespace}_terminal_selected_model:${config.accountId}`;
    try {
      localStorage.removeItem(legacyModelKey);
    } catch {
      // Storage unavailable (private mode / SSR) — nothing to clean up.
    }
  }

  const urlStore = createAgentUrlStore({
    keyFor,
    fallbackUrl: config.gatewayUrl ?? undefined,
    legacyKeyFor: config.legacyKeyFor,
    storage:
      config.ephemeralConnection === true ? createMemoryStorage() : undefined,
  });

  const historyStore = createChatHistoryStore({
    keyFor,
    defaultFolder: (config.defaultFolder ?? config.namespace) as SessionFolder,
    legacyMigration: 'one-level',
  });

  const deviceStore = createDeviceIdentityStore({
    storageKey: deviceKey,
    idbName: deviceKey,
    legacyMigrator: createLegacyDeviceKeyMigrator({
      legacyStorageKey: deviceKey,
    }),
  });

  return { urlStore, historyStore, deviceStore };
}
